// Deep-links for the account "API Connections" rows, CONSTRUCTED from the live
// connection ids/handles (the same values the ad builder publishes with) — NOT
// from the stored, manually-editable *Url fields, which drift from what Zernio
// actually has. A constructed link therefore always points at the asset the ad
// will actually use. Each builder returns null when the source id/handle is
// absent or not valid, so callers never render an empty / dead link. Pure.

// act_… (or raw digits) → the ad account's Ads Manager. Strips the act_ prefix to
// the raw digits Meta's URL expects. null when there are no digits.
export function metaAdAccountUrl(adAccountId: string | null | undefined): string | null {
  const m = (adAccountId ?? '').trim().match(/^(?:act_)?(\d+)$/);
  return m ? `https://business.facebook.com/adsmanager/manage/campaigns?act=${m[1]}` : null;
}

// Ad account + a specific campaign id → that campaign preselected in Ads Manager
// (the ad-account URL with &selected_campaign_ids appended). null when the ad
// account is invalid (via metaAdAccountUrl) OR the campaign id is missing — same
// no-dead-link posture as the other builders.
export function metaCampaignUrl(
  adAccountId: string | null | undefined,
  campaignId: string | null | undefined,
): string | null {
  const base = metaAdAccountUrl(adAccountId);
  const id = (campaignId ?? '').trim();
  return base && id !== '' ? `${base}&selected_campaign_ids=${id}` : null;
}

// Ad account + a specific ad-set id → that ad set preselected in Ads Manager.
// Same mechanism + no-dead-link posture as metaCampaignUrl: the ad-account URL
// with &selected_adset_ids appended (Ads Manager honours the selected_*_ids family
// on the same manage view). null when the ad account is invalid or the id missing.
export function metaAdSetUrl(
  adAccountId: string | null | undefined,
  adSetId: string | null | undefined,
): string | null {
  const base = metaAdAccountUrl(adAccountId);
  const id = (adSetId ?? '').trim();
  return base && id !== '' ? `${base}&selected_adset_ids=${id}` : null;
}

// Ad account + a specific ad id → that ad preselected in Ads Manager
// (&selected_ad_ids). Same family as metaCampaignUrl / metaAdSetUrl.
export function metaAdUrl(
  adAccountId: string | null | undefined,
  adId: string | null | undefined,
): string | null {
  const base = metaAdAccountUrl(adAccountId);
  const id = (adId ?? '').trim();
  return base && id !== '' ? `${base}&selected_ad_ids=${id}` : null;
}

// Numeric Facebook Page id → the Page (the numeric-id route resolves to the Page).
// null unless the id is all digits (don't build a dead link from a non-numeric id).
export function facebookPageUrl(pageId: string | null | undefined): string | null {
  const id = (pageId ?? '').trim();
  return /^\d+$/.test(id) ? `https://www.facebook.com/${id}` : null;
}

// Instagram HANDLE → the profile. IG deep-links need the handle, not the numeric
// IG id — pass the username (leading @ stripped). null when no handle, or the
// value isn't a valid handle (letters/digits/period/underscore), so a numeric IG
// id never yields a dead link.
export function instagramUrl(handle: string | null | undefined): string | null {
  const h = (handle ?? '').trim().replace(/^@+/, '').trim();
  return h !== '' && /^[A-Za-z0-9._]+$/.test(h) ? `https://www.instagram.com/${h}` : null;
}
